import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaFlagUsa, FaPlayCircle, FaShareAlt, FaShieldAlt, FaThLarge, FaVideo } from 'react-icons/fa';
import Breadcrumb from '../components/Breadcrumb.jsx';
import KeywordTopics from '../components/KeywordTopics.jsx';
import { setPageSEO } from '../utils/seo.js';
import { getCitiesForService } from '../utils/locationSolutions.js';
import { getKeywordCluster } from '../utils/keywordClusters.js';
import './Solutions.css';

const solutions = [
  {
    slug: 'testimonial-software',
    title: 'Testimonial Software',
    eyebrow: 'TESTIMONIAL SOFTWARE',
    icon: FaThLarge,
    summary: 'A complete testimonial tool for collecting, managing, and publishing customer stories from happy customers.',
    intro: 'Clientalio gives your team one testimonial service to request, review, and showcase customer proof, replacing scattered emails, screenshots, and spreadsheets with a single organized workflow.',
    benefits: [
      'One branded link to request video, text, or audio testimonials',
      'Review every response before it goes live',
      'Organize testimonials by product, team, or campaign',
      'Publish approved proof to your website in minutes'
    ],
    useCases: ['New customer onboarding', 'Sales enablement', 'Website and landing pages', 'Case study creation']
  },
  {
    slug: 'video-testimonial-software',
    title: 'Video Testimonials',
    eyebrow: 'VIDEO TESTIMONIAL SOFTWARE',
    icon: FaVideo,
    summary: 'The easiest video testimonial software to collect video testimonials from customers using nothing but their phone.',
    intro: 'A guided recording flow makes it simple for customers to say yes, while your team gets authentic testimonial videos ready to publish without hiring a production crew.',
    benefits: [
      'Guided recording flow for authentic testimonial videos',
      'Collect video testimonials without an app download',
      'Auto-generate captions for every testimonial video',
      'Publish customer testimonial videos to your site or socials'
    ],
    useCases: ['Sales enablement clips', 'Homepage hero videos', 'Social media testimonial videos', 'Ad creative and landing pages']
  },
  {
    slug: 'testimonial-collection-software',
    title: 'Testimonial Collection',
    eyebrow: 'TESTIMONIAL COLLECTION TOOL',
    icon: FaShareAlt,
    summary: 'A dependable testimonial collection tool with a simple customer testimonial form that makes it easy to collect testimonials.',
    intro: 'Send a branded testimonial form through email, SMS, or a QR code, and let customers respond from any device without creating an account.',
    benefits: [
      'Send a branded testimonial form in seconds',
      'Collect testimonials from email, SMS, or QR code',
      'Customize your client testimonial form questions',
      'Store every response in one organized library'
    ],
    useCases: ['Post-project follow-up', 'Customer success workflows', 'Event and in-person collection', 'Recurring feedback requests']
  },
  {
    slug: 'ugc-testimonial-software',
    title: 'UGC & Video Testimonials',
    eyebrow: 'UGC TESTIMONIAL SOFTWARE',
    icon: FaPlayCircle,
    summary: 'The best software for creating UGC testimonial videos that feel authentic, unscripted, and easy to reuse everywhere.',
    intro: 'Turn everyday customer moments into user-generated content by giving customers a simple prompt-led recording flow, then review and repurpose every clip as testimonial or social content.',
    benefits: [
      'Guided prompts for natural, unscripted UGC clips',
      'Collect creator-style testimonial videos at scale',
      'Approve content before it goes anywhere public',
      'Reuse one clip across ads, social, and your website'
    ],
    useCases: ['UGC ad creative', 'Social media testimonial content', 'Influencer and creator collaborations', 'Product launch proof']
  },
  {
    slug: 'social-proof-software',
    title: 'Social Proof',
    eyebrow: 'SOCIAL PROOF SOFTWARE',
    icon: FaShieldAlt,
    summary: 'Social proof software and tools that help you display trust signals exactly where buyers are deciding.',
    intro: 'Combine testimonials, reviews, and ratings in one social proof tool, then place trust signals on the pages where hesitant visitors need reassurance most.',
    benefits: [
      'Combine reviews, testimonials, and ratings in one social proof tool',
      'Show real-time activity and recent customer proof',
      'Segment social proof tools by product, plan, or audience',
      'Measure how social proof software impacts conversions'
    ],
    useCases: ['Landing page conversion', 'Pricing page trust signals', 'Booking and checkout pages', 'Multi-product marketing']
  }
];

const reveal = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } }
};

const operatingPrinciples = [
  ['Create a clear request', 'Explain why you are asking, what kind of experience is useful, and how the response may be published.'],
  ['Protect customer context', 'Keep attribution, permission, source material, and approval status connected to every response.'],
  ['Organize for retrieval', 'Tag proof by product, audience, outcome, and channel so teams can find the right story quickly.'],
  ['Measure useful adoption', 'Track response quality, approval time, publishing coverage, and the pages where proof supports conversion.']
];

function SolutionsHub() {
  useEffect(() => setPageSEO({
    title: 'Testimonial & Social Proof Solutions | Clientalio',
    description: 'Explore Clientalio solutions for testimonial software, video testimonials, testimonial collection, UGC content, and social proof.',
    canonical: 'https://clientalio.com/solutions',
    ogTitle: 'Testimonial & Social Proof Solutions | Clientalio',
    ogDescription: 'Practical solutions for collecting, managing, and publishing authentic customer proof.',
    twitterTitle: 'Clientalio Solutions',
    twitterDescription: 'Testimonial software, video testimonials, collection tools, UGC, and social proof in one platform.'
  }), []);

  return (
    <main className="solutions-page">
      <div className="solutions-breadcrumb"><div className="container"><Breadcrumb currentLabel="Solutions" /></div></div>

      <section className="solutions-hero">
        <div className="container solutions-hero-grid">
          <motion.div initial="hidden" animate="visible" variants={reveal}>
            <span className="solutions-eyebrow">CUSTOMER PROOF, ONE PLATFORM</span>
            <h1>Solutions built around how you collect and use customer proof.</h1>
            <p>Whether you need testimonial software, video testimonials, a collection tool, UGC content, or social proof widgets, Clientalio gives your team one connected workflow.</p>
            <a className="solutions-primary-action" href="#solutions-grid">Explore solutions <FaArrowRight /></a>
          </motion.div>
          <motion.aside initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55 }} className="solutions-hero-note">
            <span>START HERE</span>
            <h2>Every solution shares the same simple workflow.</h2>
            <p>Ask, collect, review, and publish, without waiting on a developer or juggling separate tools.</p>
            <Link to="/signup">Start free <FaArrowRight /></Link>
          </motion.aside>
        </div>
      </section>

      <section className="solutions-grid-section" id="solutions-grid">
        <div className="container">
          <div className="solutions-section-heading">
            <span>CHOOSE YOUR SOLUTION</span>
            <h2>Solutions for every stage of customer proof</h2>
            <p>Each solution focuses on a specific job, from asking for feedback to displaying it where buyers decide.</p>
          </div>
          <motion.div className="solutions-grid" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.08 }}>
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.article key={solution.slug} variants={reveal} className="solution-card">
                  <Link to={`/solutions/${solution.slug}`}>
                    <div className="solution-card-top"><span>0{index + 1}</span><Icon /></div>
                    <h3>{solution.title}</h3>
                    <p>{solution.summary}</p>
                    <span className="solution-card-action">Explore solution <FaArrowRight /></span>
                  </Link>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="solutions-method">
        <div className="container solutions-method-grid">
          <div><span className="solutions-eyebrow">A SIMPLE PRACTICE</span><h2>Ask clearly. Collect naturally. Publish responsibly.</h2></div>
          <div className="solutions-method-list">
            <p><strong>01</strong><span><b>Start with the right solution.</b> Match the tool to the proof you need, whether that is video, text, or widgets.</span></p>
            <p><strong>02</strong><span><b>Remove friction.</b> Let customers respond from any device without creating an account.</span></p>
            <p><strong>03</strong><span><b>Publish with confidence.</b> Review and approve every response before it reaches your audience.</span></p>
          </div>
        </div>
      </section>

      <section className="solutions-cta">
        <div className="container solutions-cta-inner"><div><span>PUT YOUR SOLUTION TO WORK</span><h2>Start collecting customer proof today.</h2><p>Create a branded request, share one link, and review every response before it goes live.</p></div><Link to="/signup">Start free <FaArrowRight /></Link></div>
      </section>
    </main>
  );
}

function SolutionDetail({ solution }) {
  useEffect(() => setPageSEO({
    title: `${solution.title} | Clientalio Solutions`,
    description: solution.summary,
    keywords: getKeywordCluster(solution.slug),
    canonical: `https://clientalio.com/solutions/${solution.slug}`,
    ogTitle: `${solution.title} by Clientalio`,
    ogDescription: solution.summary,
    twitterTitle: `${solution.title} | Clientalio`,
    twitterDescription: solution.summary
  }), [solution]);

  const related = solutions.filter((item) => item.slug !== solution.slug).slice(0, 3);
  const Icon = solution.icon;
  const cities = getCitiesForService(solution.slug);

  return (
    <main className="solution-detail-page">
      <div className="solutions-breadcrumb"><div className="container"><Breadcrumb items={[{ label: 'Solutions', to: '/solutions' }]} currentLabel={solution.title} /></div></div>

      <section className="solution-detail-hero">
        <div className="container solution-detail-hero-grid">
          <motion.div initial="hidden" animate="visible" variants={reveal}>
            <span className="solutions-eyebrow">{solution.eyebrow}</span>
            <h1>{solution.title}</h1>
            <p>{solution.summary}</p>
            <div className="solution-detail-actions"><a href="/signup">Start collecting for free <FaArrowRight /></a><Link to="/solutions">All solutions</Link></div>
          </motion.div>
          <motion.aside initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55 }} className="solution-detail-summary">
            <Icon />
            <span>WHAT YOU GET</span>
            <ul>{solution.benefits.map((benefit) => <li key={benefit}><FaCheckCircle /> {benefit}</li>)}</ul>
          </motion.aside>
        </div>
      </section>

      <KeywordTopics slug={solution.slug} />

      <section className="solution-guide">
        <div className="container solution-guide-grid">
          <article>
            <span className="solutions-eyebrow">HOW IT WORKS</span>
            <h2>A practical approach to {solution.title.toLowerCase()}</h2>
            <p>{solution.intro}</p>
            <p>Every response is reviewed before publishing, so your team stays in control of what customers see while keeping the process fast for the customers responding.</p>
          </article>
          <aside>
            <h3>Where it fits</h3>
            {solution.useCases.map((useCase) => <p key={useCase}><FaCheckCircle /> {useCase}</p>)}
          </aside>
        </div>
      </section>

      <section className="solution-workflow">
        <div className="container">
          <div className="solutions-section-heading"><span>FROM REQUEST TO PUBLISHED PROOF</span><h2>A simple, repeatable workflow</h2></div>
          <div className="solution-steps">
            <article><span>1</span><div><h3>Ask</h3><p>Send a branded request built for {solution.title.toLowerCase()}, explain how the response will be used, and focus the prompts on one customer outcome.</p></div></article>
            <article><span>2</span><div><h3>Collect and review</h3><p>Let customers respond from any device, then check the result for specificity, attribution, consent, and relevance before approval.</p></div></article>
            <article><span>3</span><div><h3>Publish and reuse</h3><p>Place the strongest proof beside the offer it supports, then adapt the approved story for sales, web, email, and campaign channels.</p></div></article>
          </div>
        </div>
      </section>

      <section className="solution-guide">
        <div className="container">
          <div className="solutions-section-heading"><span>OPERATE AT SCALE</span><h2>Build a dependable {solution.title.toLowerCase()} program</h2><p>Use a consistent operating model so collection quality does not depend on one person or one campaign.</p></div>
          <div className="solution-steps">
            {operatingPrinciples.map(([title, description], index) => <article key={title}><span>{index + 1}</span><div><h3>{title}</h3><p>{description}</p></div></article>)}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="solution-related">
          <div className="container">
            <div className="solutions-section-heading"><span>KEEP EXPLORING</span><h2>Related solutions</h2></div>
            <div className="solution-related-grid">
              {related.map((item) => (
                <Link to={`/solutions/${item.slug}`} key={item.slug}>
                  <span>{item.eyebrow}</span>
                  <h3>{item.title}</h3>
                  <p>{item.summary}</p>
                  <b>Explore solution <FaArrowRight /></b>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {cities.length > 0 && (
        <section className="solution-also-support">
          <div className="container solution-also-support-inner">
            <span className="solution-also-support-flag"><FaFlagUsa /></span>
            <span className="solution-also-support-text">We also support</span>
            <span className="solution-also-support-flag"><FaFlagUsa /></span>
            <ul className="solution-city-list">
              {cities.map((city) => (
                <li key={city.slug}>
                  <Link to={`/solutions/${city.slug}`}>{city.cityName}</Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="solutions-cta">
        <div className="container solutions-cta-inner"><div><span>PUT {solution.title.toUpperCase()} TO WORK</span><h2>Ready to get started?</h2><p>Create your free Clientalio account and publish your first customer proof without waiting on a developer.</p></div><a href="/signup">Start free today</a></div>
      </section>
    </main>
  );
}

function Solutions() {
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  const slug = parts[0] === 'solutions' ? parts[1] : parts[0];
  const solution = solutions.find((item) => item.slug === slug);

  return solution ? <SolutionDetail solution={solution} /> : <SolutionsHub />;
}

export { solutions };
export default Solutions;
